import { Building2, Clock, CreditCard, MapPin, Plug, ScrollText, ShieldCheck, Users, type LucideIcon } from 'lucide-react';
import { NavLink, Navigate, Outlet } from 'react-router';
import type { Permission } from '@barbeiropro/contracts';
import { PageHeader } from '@/components/shared/page-header';
import { useSession } from '@/lib/session';
import { cn } from '@/lib/utils';

interface Item {
  to: string;
  label: string;
  icon: LucideIcon;
  perm?: Permission;
}

const ITEMS: Item[] = [
  { to: 'barbearia', label: 'Barbearia', icon: Building2, perm: 'settings.manage' },
  { to: 'unidades', label: 'Unidades e cadeiras', icon: MapPin, perm: 'settings.manage' },
  { to: 'horarios', label: 'Horário de funcionamento', icon: Clock, perm: 'settings.manage' },
  { to: 'regras', label: 'Regras de agendamento', icon: ShieldCheck, perm: 'settings.manage' },
  { to: 'equipe', label: 'Equipe e permissões', icon: Users, perm: 'members.manage' },
  { to: 'assinatura', label: 'Assinatura e plano', icon: CreditCard, perm: 'settings.manage' },
  { to: 'integracoes', label: 'Integrações', icon: Plug, perm: 'settings.manage' },
  { to: 'auditoria', label: 'Auditoria', icon: ScrollText, perm: 'audit.read' },
];

function useVisibleItems() {
  const { can } = useSession();
  return ITEMS.filter((i) => !i.perm || can(i.perm));
}

export function SettingsIndex() {
  const items = useVisibleItems();
  return <Navigate to={items[0] ? `/app/configuracoes/${items[0].to}` : '/app/conta'} replace />;
}

export function SettingsLayout() {
  const items = useVisibleItems();
  return (
    <div>
      <PageHeader title="Configurações" description="Dados da barbearia, regras de agendamento, equipe e integrações." />
      <div className="grid gap-6 lg:grid-cols-[15rem_1fr]">
        <nav aria-label="Seções das configurações" className="-mx-4 overflow-x-auto px-4 lg:mx-0 lg:px-0">
          <ul className="flex gap-1 lg:flex-col">
            {items.map((i) => (
              <li key={i.to} className="shrink-0">
                <NavLink
                  to={i.to}
                  className={({ isActive }) =>
                    cn(
                      'flex items-center gap-2.5 rounded-md border-l-2 px-3 py-2 text-sm whitespace-nowrap transition-colors outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50',
                      isActive ? 'border-brass bg-accent font-medium text-accent-foreground' : 'border-transparent text-muted-foreground hover:bg-muted hover:text-foreground',
                    )
                  }
                >
                  <i.icon className="size-4" aria-hidden />
                  {i.label}
                </NavLink>
              </li>
            ))}
          </ul>
        </nav>
        <div className="min-w-0">
          <Outlet />
        </div>
      </div>
    </div>
  );
}
